import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Auto-Refresh Hook
 * 
 * Provides automatic polling functionality with configurable interval.
 * Preserves filter and search state during refresh.
 * 
 * Requirements: 6.1, 6.2, 6.3, 6.4, 6.5
 */

export interface UseAutoRefreshOptions {
  /**
   * Refresh interval in milliseconds
   * Default: 30000 (30 seconds)
   */
  interval?: number;
  
  /**
   * Whether auto-refresh is enabled
   * Default: true
   */
  enabled?: boolean;
  
  /**
   * Callback function to execute on each refresh
   */
  onRefresh: () => void | Promise<void>;
}

export interface UseAutoRefreshReturn {
  /**
   * Timestamp of last successful refresh
   */
  lastRefresh: Date | null;
  
  /**
   * Whether a refresh is currently in progress
   */
  isRefreshing: boolean;
  
  /**
   * Manually trigger a refresh
   */
  refresh: () => Promise<void>;
  
  /**
   * Enable or disable auto-refresh
   */
  setEnabled: (enabled: boolean) => void;
  
  /**
   * Whether auto-refresh is currently enabled
   */
  enabled: boolean;
}

/**
 * Custom hook for auto-refresh functionality
 * 
 * @param options - Configuration options
 * @returns Auto-refresh control interface
 */
export function useAutoRefresh(options: UseAutoRefreshOptions): UseAutoRefreshReturn {
  const {
    interval = 30000, // 30 seconds default
    enabled: initialEnabled = true,
    onRefresh,
  } = options;
  
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [enabled, setEnabled] = useState(initialEnabled);
  
  // Use refs to avoid recreating interval on every render
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const onRefreshRef = useRef(onRefresh);
  const isInitialMount = useRef(true);
  const isRefreshingRef = useRef(false);
  
  // Keep refs up to date
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);
  
  useEffect(() => {
    isRefreshingRef.current = isRefreshing;
  }, [isRefreshing]);
  
  /**
   * Execute refresh callback
   */
  const refresh = useCallback(async () => {
    if (isRefreshingRef.current) {
      return; // Prevent concurrent refreshes
    }
    
    setIsRefreshing(true);
    
    try {
      await onRefreshRef.current();
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Auto-refresh failed:', error);
      // Don't update lastRefresh on error
    } finally {
      setIsRefreshing(false);
    }
  }, []);
  
  /**
   * Set up auto-refresh interval
   */
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Only set up interval if enabled
    if (enabled) {
      // Execute initial refresh on first mount
      if (isInitialMount.current) {
        refresh();
        isInitialMount.current = false;
      }
      
      // Set up periodic refresh
      intervalRef.current = setInterval(() => {
        refresh();
      }, interval);
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, interval, refresh]);
  
  return {
    lastRefresh,
    isRefreshing,
    refresh,
    setEnabled,
    enabled,
  };
}
